import { useState } from 'react';
import type { MetafieldOwner } from '@dunea/plugin-sdk';
import { PICKABLE_TYPES, fieldTypeLabel } from '~/components/workbench/fields';
import type { FieldType } from '~/components/workbench/fields/sdk';
import { apiDefineMetafield, apiDeleteDataDefinition, type DataDefinition } from '~/lib/api/data';
import { classNames } from '~/utils/classNames';
import {
  BTN_GHOST,
  BTN_SECONDARY,
  CARD,
  ErrorBanner,
  LoadError,
  Pill,
  Section,
  Select,
  SkeletonCard,
  Spinner,
  Switch,
} from '~/components/workbench/tools/payments/ui';
import type { Resource } from '~/components/workbench/tools/payments/use-resource';
import {
  FIELD_OWNERS,
  apiErrorMessage,
  canEditDefinition,
  namespaceLabel,
  ownerLabel,
  parseOptions,
  toKey,
} from './content-model';

// Reference types need a target the simple form can't pick; define those in chat.
const FIELD_TYPES = PICKABLE_TYPES.filter((t) => t !== 'metaobject_reference');

/** Custom fields (metafield definitions): extra values on products, orders, pages, the site… */
export function FieldDefinitionsView({ appId, defs }: { appId: string; defs: Resource<DataDefinition[]> }) {
  const [adding, setAdding] = useState(false);
  const fields = (defs.data ?? []).filter((d) => d.kind === 'metafield');
  const groups = FIELD_OWNERS.map(([owner, label]) => ({
    owner,
    label,
    items: fields.filter((f) => f.ownerType === owner),
  })).filter((g) => g.items.length);

  return (
    <Section
      title="Custom fields"
      icon="i-ph:textbox"
      aside={
        !adding && (
          <button type="button" className={BTN_SECONDARY} onClick={() => setAdding(true)}>
            <div className="i-ph:plus text-[13px]" />
            New field
          </button>
        )
      }
    >
      {adding && (
        <NewFieldForm
          appId={appId}
          onCancel={() => setAdding(false)}
          onSaved={(saved) => {
            defs.setData((list) => [...(list ?? []).filter((d) => d.id !== saved.id), saved]);
            setAdding(false);
          }}
        />
      )}

      {defs.loading && !defs.data ? (
        <SkeletonCard lines={2} />
      ) : defs.error ? (
        <LoadError message="Could not load custom fields." onRetry={() => void defs.reload()} />
      ) : !groups.length ? (
        !adding && (
          <div className={classNames(CARD, 'space-y-1.5 px-4 py-6 text-center')}>
            <div className="i-ph:textbox mx-auto text-xl text-faint" />
            <p className="text-[12px] leading-relaxed text-muted">
              Add fields like "Care guide" to products or "Gift note" to orders. You fill them in on each product or
              order.
            </p>
          </div>
        )
      ) : (
        groups.map((g) => (
          <div key={g.owner} className="space-y-1.5">
            <div className="text-[11px] text-secondary">{g.label}</div>
            <ul className={classNames(CARD, 'divide-y divide-hairline')}>
              {g.items.map((d) => (
                <FieldRow
                  key={d.id}
                  appId={appId}
                  def={d}
                  onDeleted={() => defs.setData((list) => (list ?? []).filter((x) => x.id !== d.id))}
                />
              ))}
            </ul>
          </div>
        ))
      )}
    </Section>
  );
}

function FieldRow({ appId, def, onDeleted }: { appId: string; def: DataDefinition; onDeleted: () => void }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const from = namespaceLabel(def.namespace);

  const remove = async () => {
    setBusy(true);
    setError(null);

    try {
      await apiDeleteDataDefinition(appId, def.id);
      onDeleted();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not delete the field.'));
      setBusy(false);
    }
  };

  return (
    <li className="space-y-2 px-4 py-2.5">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-[13px] text-primary">{def.name}</span>
            {def.access.storefront === 'public' && <Pill tone="accent" label="On site" />}
            {from && <Pill tone="info" label={from} />}
          </div>
          <div className="truncate text-[11px] text-muted">
            {def.field ? fieldTypeLabel(def.field.type) : ''}
            {def.field?.list ? ' (several)' : ''} · <code className="font-geist-mono">{def.key}</code>
          </div>
        </div>
        {canEditDefinition(def) &&
          (confirm ? (
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                className={classNames(BTN_GHOST, '!text-danger')}
                disabled={busy}
                onClick={() => void remove()}
              >
                {busy ? <Spinner /> : 'Delete with values'}
              </button>
              <button type="button" className={BTN_GHOST} onClick={() => setConfirm(false)}>
                Keep
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="ui-btn-icon shrink-0"
              aria-label={`Delete ${def.name}`}
              onClick={() => setConfirm(true)}
            >
              <span className="i-ph:trash text-[13px] text-muted" />
            </button>
          ))}
      </div>
      {error && <ErrorBanner message={error} />}
    </li>
  );
}

function NewFieldForm({
  appId,
  onCancel,
  onSaved,
}: {
  appId: string;
  onCancel: () => void;
  onSaved: (saved: DataDefinition) => void;
}) {
  const [owner, setOwner] = useState<MetafieldOwner>('product');
  const [name, setName] = useState('');
  const [type, setType] = useState<FieldType>('single_line_text');
  const [options, setOptions] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const key = toKey(name);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!key) {
      setError('Give the field a name.');
      return;
    }

    if (type === 'select' && !parseOptions(options).length) {
      setError('Add at least one option.');
      return;
    }

    setBusy(true);
    setError(null);

    try {
      onSaved(
        await apiDefineMetafield(appId, {
          ownerType: owner,
          key,
          name: name.trim(),
          field: { type, ...(type === 'select' ? { options: parseOptions(options) } : {}) },
          access: { storefront: isPublic && owner !== 'order' ? 'public' : 'none' },
        }),
      );
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not add the field.'));
      setBusy(false);
    }
  };

  return (
    <form className={classNames(CARD, 'space-y-3 p-4')} onSubmit={(e) => void save(e)}>
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="block space-y-1.5">
          <span className="block text-[11px] text-secondary">On</span>
          <Select label="Resource" value={owner} onChange={(v) => setOwner(v as MetafieldOwner)}>
            {FIELD_OWNERS.map(([o, label]) => (
              <option key={o} value={o}>
                {label}
              </option>
            ))}
          </Select>
        </label>
        <label className="block space-y-1.5">
          <span className="block text-[11px] text-secondary">Type</span>
          <Select label="Type" value={type} onChange={(v) => setType(v as FieldType)}>
            {FIELD_TYPES.map((t) => (
              <option key={t} value={t}>
                {fieldTypeLabel(t)}
              </option>
            ))}
          </Select>
        </label>
      </div>
      <label className="block space-y-1.5">
        <span className="block text-[11px] text-secondary">Name</span>
        <input className="ui-input" placeholder="Care guide" value={name} onChange={(e) => setName(e.target.value)} />
        {key && (
          <span className="block text-[11px] text-muted">
            Key <code className="font-geist-mono">{key}</code> on {ownerLabel(owner).toLowerCase()}
          </span>
        )}
      </label>
      {type === 'select' && (
        <textarea
          aria-label="Options"
          rows={2}
          className="ui-input resize-y"
          placeholder="One option per line"
          value={options}
          onChange={(e) => setOptions(e.target.value)}
        />
      )}
      {owner !== 'order' && <Switch checked={isPublic} onChange={setIsPublic} label="Your site can show this field" />}
      {error && <ErrorBanner message={error} />}
      <div className="flex items-center justify-end gap-1.5">
        <button type="button" className={BTN_GHOST} onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="ui-btn-accent !px-4 !py-2 !text-[12px]" disabled={busy}>
          {busy && <Spinner />}
          Add field
        </button>
      </div>
    </form>
  );
}
